import React, { useState, useEffect, useRef } from "react";
import { notification, Input, Modal, Spin, Button } from "antd";
import axios from "axios";

import { filmStore } from "@/store/film";
import { getResponseErrorMessage } from '@/utils/api-service'

import api from "@/api/index";

import ModalImportAccept from "@components/video-control/ModalImportAccept";

import './styles/block-get-info-by-id.scss'

interface Props {
  type: string;
  defaultData: any;
  name_original: any;
  isEdit?: boolean;

  update: (data:any) => void;
  updateOption?: (key:string, value:any) => void;
}

function BlockGetInfoById({type, defaultData, update, updateOption, name_original, isEdit = false}: Props) {
  const [kinopoiskId, setKinopoiskId] = useState<string>(defaultData?.kinopoiskId || '');
  const [IMDB, setIMDB] = useState<string>(defaultData?.IMDB || '');

  const [importModalData, setImportModalData] = useState(null);
  const [isShowModalImport, setIsShowModalImport] = useState<boolean>(false)
  const [importModalType, setImportModalType] = useState<string | null>(null)

  const [categories, setCategories] = useState<any[]>([])
  const [isLoadingInfo, setIsLoadingInfo] = useState<boolean>(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  const getInfoById = async (type:string) => {
    try {
      const controller = new AbortController()
      abortControllerRef.current = controller
      setIsLoadingInfo(true)

      let response = null

      if(type === 'kinopoisk') {
        response = await api.video.getKinopoiskId(kinopoiskId, name_original, controller.signal);
        response = response.data.data

      } else {
        response = await api.video.getByImdId(IMDB, name_original, controller.signal);
        response = response.data.data;
      }

      response.name = response.title || ''
      response.name_original = response.title_original || ''

      setImportModalData(response)
      setImportModalType(type)
      setIsShowModalImport(true)

    } catch (err) {
      if (axios.isCancel(err)) {
        // Пользователь нажал «Отмена» — запрос прерван, ничего не показываем
        return
      }

      console.log(err);

      let message = 'Ошибка'

      if(type === 'kinopoisk') {
        message = 'Ошибка при получении видео по Киопоиск ID'
      } else {
        message = 'Ошибка при получении видео по IMDB'
      }

      const messageFormatter = getResponseErrorMessage(err,message)

      notification.error({
        message: messageFormatter
      });
    } finally {
      setIsLoadingInfo(false)
      abortControllerRef.current = null
    }
  };

  const cancelImport = () => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    setIsLoadingInfo(false)
  }

  const updateValue = (key, value) => {
    if(type === 'film') {
      filmStore.setFilmOption(key, value);
      updateOption(key, value)
    }

    if(type === 'serial') {
      updateOption(key, value)
    }
  }

  const acceptImport = () => {
    setIsShowModalImport(false)

    let message = `Информация по ${importModalType === 'kinopoisk' ? 'кинопооиск' : 'IMDB' } успешно найдена`;
    notification.success({
      message: message
    });

    if(update) {
      update(importModalData)
    }
  }

  const closeModalImport = () => {
    setIsShowModalImport(false)
  }

  const getCategories = async() => {
    try {
      const response = await api.categories.getCategoriesContentManager()

      if(Array.isArray(response.data.data.categories)) {
        setCategories(response.data.data.categories)
      } else {
        setCategories([])
      }

    } catch(err) {
      console.error(err)
    }
  }

  useEffect(() => {
    // ID-поля подхватываем из store только при редактировании —
    // иначе при создании нового фильма/сериала остаются старые значения
    if (isEdit) {
      if(filmStore.film.kinopoisk_id) {
        setKinopoiskId(filmStore.film.kinopoisk_id)
      }

      if(filmStore.film.imdb_id) {
        setIMDB(filmStore.film.imdb_id)
      }
    }

    getCategories()
  }, [])

  return (
    <div className="film-main-info__block-ids">
      <div className="film-main-info__ids">
        <div className="form-group">
          <label className="form-group__label">Kinopoisk ID</label>
          <Input
            value={kinopoiskId}
            onChange={(e) => {
              setKinopoiskId(e.target.value);
              updateValue('kinopoisk_id', e.target.value)
            }}
            placeholder="Kinopoisk ID"
          />
        </div>

        <div className="form-group">
          <label className="form-group__label">IMDB ID</label>
          <Input
            value={IMDB}
            onChange={(e) => {
              setIMDB(e.target.value);
              updateValue('imdb_id', e.target.value)
            }}
            placeholder="IMDB ID"
          />
        </div>
      </div>

      <div className="film-main-info__links">
        <button
          className="app-btn default film-main-info__btn-info"
          onClick={() => getInfoById("kinopoisk")}
          disabled={!kinopoiskId || isLoadingInfo}
        >
          Получить по Kinopoisk
        </button>
        <button
          className="film-main-info__btn-info app-btn default"
          disabled={!IMDB || isLoadingInfo}
          onClick={() => getInfoById("IMDB")}
        >
          Получить по IMDB
        </button>
      </div>

      <Modal
          title="Загрузка данных"
          open={isLoadingInfo}
          onCancel={cancelImport}
          footer={[
              <Button key="cancel" onClick={cancelImport}>Отмена</Button>
          ]}
          maskClosable={false}
          closable={false}
      >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0' }}>
              <Spin />
              <span>Подождите, обрабатывается...</span>
          </div>
      </Modal>

      <Modal
          title="Импорт данных из Kinopoisk/IMDB"
          closable={{ "aria-label": "Custom Close Button" }}
          open={isShowModalImport}
          onOk={acceptImport}
          onCancel={closeModalImport}
          okText="Импортировать"
          cancelText="Отмена"
      >
        <ModalImportAccept data={importModalData} categories={categories} />
      </Modal>
    </div>
  );
}

export default BlockGetInfoById;
